import React, { forwardRef } from 'react';
import { Box, Typography, Divider, Paper, Stack, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material';
import { getUploadUrl } from '../../config/api';

const IndustrialPayslip = forwardRef(({ payslip, tenantSettings }: any, ref: any) => {
    if (!payslip) return null;

    const breakdown = payslip.breakdown || {};
    const deductions = breakdown.deductions || [];
    const allowances = breakdown.allowances || [];
    const basicSalary = Number(breakdown.basicSalary || payslip.basicSalary || 0);
    const grossSalary = Number(breakdown.grossSalary || basicSalary);
    const netSalary = Number(payslip.netSalary || 0);

    const statutoryDeductions = deductions.filter((d: any) => d.isStatutory);
    const otherDeductions = deductions.filter((d: any) => !d.isStatutory);

    const labelStyle = { color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 };
    const valueStyle = { fontWeight: 800, color: '#2D3748', fontSize: '1rem' };

    return (
        <div ref={ref} style={{ padding: '50px', backgroundColor: 'white', color: 'black', fontFamily: '"Inter", "Arial", sans-serif' }}>
            {/* Professional Header Section */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
                <Stack direction="row" spacing={3} alignItems="center">
                    {tenantSettings?.logo && (
                        <img 
                            src={getUploadUrl(tenantSettings.logo)} 
                            alt="Company Logo" 
                            style={{ height: '90px', width: 'auto', objectFit: 'contain' }} 
                        />
                    )}
                    <Box>
                        <Typography variant="h3" fontWeight={900} sx={{ color: '#1A365D', mt: 0, mb: 0.5 }}>
                            {tenantSettings?.name || 'Organization Name'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" fontWeight={500}>
                            {tenantSettings?.address || 'Official Address'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {tenantSettings?.email} | {tenantSettings?.phone}
                        </Typography>
                    </Box>
                </Stack>
                <Box textAlign="right">
                    <Typography variant="h4" fontWeight={900} color="#E2E8F0" sx={{ letterSpacing: '2px', mb: 1 }}>PAYSLIP</Typography>
                    <Chip 
                        label={payslip.status} 
                        sx={{ 
                            fontWeight: 900, 
                            bgcolor: payslip.status === 'PAID' ? '#C6F6D5' : '#FEFCBF', 
                            color: payslip.status === 'PAID' ? '#22543D' : '#744210',
                            borderRadius: '4px'
                        }} 
                    />
                </Box>
            </Box>

            <Divider sx={{ mb: 4, bgcolor: '#1A365D', height: '2px' }} />

            {/* Employee Information Dashboard */}
            <Box mb={5} p={3} sx={{ bgcolor: '#EDF2F7', borderRadius: '4px', border: '1px solid #E2E8F0' }}>
                <Grid container spacing={4}>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>EMPLOYEE NAME</Typography>
                        <Typography sx={valueStyle}>{payslip.staffProfile?.name || payslip.staffProfile?.user?.name || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>EMPLOYEE NUMBER</Typography>
                        <Typography sx={valueStyle}>{payslip.staffProfile?.employeeNo || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>PAY PERIOD</Typography>
                        <Typography sx={valueStyle}>{payslip.month}/{payslip.year}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>DEPARTMENT</Typography>
                        <Typography sx={valueStyle}>{payslip.staffProfile?.department || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>ENGAGEMENT DATE</Typography>
                        <Typography sx={valueStyle}>
                            {payslip.staffProfile?.dateOfJoining ? new Date(payslip.staffProfile.dateOfJoining).toLocaleDateString() : 'N/A'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>ACCRUED LEAVE</Typography>
                        <Typography sx={valueStyle}>{payslip.staffProfile?.leaveBalance || 0} Days</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>DESIGNATION</Typography>
                        <Typography sx={valueStyle}>{payslip.staffProfile?.position || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography sx={labelStyle}>PAYMENT MODE</Typography>
                        <Typography sx={valueStyle} color="primary.main">{payslip.paymentMethod || 'CASH'}</Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Financial Breakdown Table */}
            <Box display="flex" gap={4} mb={5}>
                {/* EARNINGS COLUMN */}
                <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight={900} color="#1A365D" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: '12px', height: '12px', bgcolor: 'success.main', borderRadius: '50%' }} />
                        EARNINGS & ALLOWANCES
                    </Typography>
                    <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800 }}>DESCRIPTION</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800 }}>AMOUNT</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Basic Salary</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>${basicSalary.toFixed(2)}</TableCell>
                                </TableRow>
                                {allowances.map((a: any, i: number) => (
                                    <TableRow key={i}>
                                        <TableCell>{a.name}</TableCell>
                                        <TableCell align="right">${Number(a.amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableCell sx={{ fontWeight: 800 }}>TOTAL EARNINGS</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 900, color: 'success.dark' }}>${grossSalary.toFixed(2)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {/* DEDUCTIONS COLUMN */}
                <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight={900} color="#1A365D" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: '12px', height: '12px', bgcolor: 'error.main', borderRadius: '50%' }} />
                        STATUTORY & VOLUNTARY DEDUCTIONS
                    </Typography>
                    <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800 }}>DESCRIPTION</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800 }}>DEDUCTED</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {statutoryDeductions.length > 0 || otherDeductions.length > 0 ? (
                                    deductions.map((d: any, i: number) => (
                                        <TableRow key={i}>
                                            <TableCell>{d.name} {d.isStatutory && '(Statutory)'}</TableCell>
                                            <TableCell align="right" sx={{ color: 'error.main' }}>-${Number(d.calculatedAmount).toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow><TableCell colSpan={2} align="center"><Typography variant="caption" color="text.secondary">No deductions applicable</Typography></TableCell></TableRow>
                                )}
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableCell sx={{ fontWeight: 800 }}>TOTAL DEDUCTIONS</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 900, color: 'error.dark' }}>${Number(payslip.totalDeductions).toFixed(2)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>

            {/* Annual Summary (YTD) Section */}
            <Box mb={5}>
                <Typography variant="subtitle2" fontWeight={800} color="text.secondary" mb={1} sx={{ letterSpacing: '0.1em' }}>ANNUAL SUMMARY (YEAR-TO-DATE)</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 4 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>TOTAL TAX YEAR INCOME</Typography>
                            <Typography sx={{ fontWeight: 800 }}>${Number(payslip.ytdEarnings || 0).toFixed(2)}</Typography>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>TOTAL TAX PAID (YTD)</Typography>
                            <Typography sx={{ fontWeight: 800 }}>${Number(payslip.ytdTax || 0).toFixed(2)}</Typography>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>TOTAL YEAR DEDUCTIONS</Typography>
                            <Typography sx={{ fontWeight: 800 }}>${Number(payslip.ytdDeductions || 0).toFixed(2)}</Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* Final Net Pay Computation */}
            <Box mb={6} p={4} sx={{ bgcolor: '#1A365D', color: 'white', borderRadius: '4px', textAlign: 'center' }}>
                <Typography variant="h6" sx={{ opacity: 0.8, fontSize: '0.9rem', mb: 1, letterSpacing: '1px' }}>TOTAL NET PAYABLE AMOUNT</Typography>
                <Typography variant="h2" fontWeight={900} sx={{ letterSpacing: '2px' }}>
                    ${netSalary.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
            </Box>

            {/* Professional Footer & Watermark */}
            <Box mt={10} textAlign="center" sx={{ borderTop: '1px solid #E2E8F0', pt: 4 }}>
                <Typography variant="caption" display="block" sx={{ fontWeight: 600, letterSpacing: '1px', opacity: 0.5 }}>
                    This is an official document generated by the SquareEdge Payroll Engine.
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#1A365D' }}>
                    Generated on 4/7/2026 via squareedge school accountiing system Payroll Engine.
                </Typography>
            </Box>
        </div>
    );
});

export default IndustrialPayslip;
